import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { cn } from '../../../utils/class-utils';

@Component({
    selector: 'jjames-modal',
    standalone: true,
    imports: [CommonModule],
    template: `
    <!-- Modal overlay -->
    @if (isOpen) {
      <div 
        class="fixed inset-0 z-50 overflow-y-auto"
        (click)="onOverlayClick($event)"
      >
        <!-- Background overlay -->
        <div class="fixed inset-0 bg-black bg-opacity-50 transition-opacity"></div>
        
        <!-- Modal container -->
        <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <!-- Modal panel -->
          <div [class]="modalClasses" (click)="$event.stopPropagation()">
            <!-- Header -->
            @if (showHeader) {
              <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <h3 class="text-lg font-semibold text-gray-900">
                  {{ title }}
                </h3>
                @if (closable) {
                  <button
                    type="button"
                    class="text-gray-400 hover:text-gray-600 focus:outline-none"
                    (click)="close()"
                  >
                    <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                }
              </div>
            }
            
            <!-- Content -->
            <div [class]="contentClasses">
              <ng-content></ng-content>
            </div>
            
            <!-- Footer -->
            @if (showFooter) {
              <div class="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
                <ng-content select="[slot=footer]"></ng-content>
              </div>
            }
          </div>
        </div>
      </div>
    }
  `,
    styles: []
})
export class JJamesModalComponent implements OnInit, OnDestroy {
    @Input() isOpen = false;
    @Input() title?: string;
    @Input() size: 'sm' | 'md' | 'lg' | 'xl' | 'full' = 'md';
    @Input() closable = true;
    @Input() closeOnOverlayClick = true;
    @Input() showHeader = true;
    @Input() showFooter = false;

    @Output() closed = new EventEmitter<void>();
    @Output() opened = new EventEmitter<void>();

    get modalClasses(): string {
        const baseClasses = 'relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full';
        const sizeClasses = this.getSizeClasses();

        return cn(baseClasses, sizeClasses);
    }

    get contentClasses(): string {
        const baseClasses = 'px-6 py-4';
        return baseClasses;
    }

    private getSizeClasses(): string {
        const sizes: Record<string, string> = {
            sm: 'sm:max-w-sm',
            md: 'sm:max-w-md',
            lg: 'sm:max-w-lg',
            xl: 'sm:max-w-xl',
            full: 'sm:max-w-full sm:mx-4'
        };
        return sizes[this.size];
    }

    ngOnInit(): void {
        if (this.isOpen) {
            this.handleOpen();
        }
    }

    ngOnDestroy(): void {
        this.handleClose();
    }

    close(): void {
        this.isOpen = false;
        this.handleClose();
        this.closed.emit();
    }

    onOverlayClick(event: Event): void {
        if (this.closeOnOverlayClick && event.target === event.currentTarget) {
            this.close();
        }
    }

    private handleOpen(): void {
        // Prevent body scroll
        document.body.style.overflow = 'hidden';
        this.opened.emit();
    }

    private handleClose(): void {
        // Restore body scroll
        document.body.style.overflow = '';
    }
}